import { HttpErrorResponse, HttpEvent, HttpHandler, HttpInterceptor, HttpRequest, HttpResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { from, Observable, of, throwError } from 'rxjs';
import { catchError, switchMap } from 'rxjs/operators';
import {
    handsFailureToCaller,
    isCallerHandledCall,
    isSilencedBackgroundCall,
    silencesBackgroundError,
} from './BackgroundCallErrorHelper';

/**
 * Keeps ABP's error dialog away from the silent background calls of Global Conversations (BackgroundCallErrorHelper,
 * finding F1). For a failed silenced call it re-throws an equivalent HttpErrorResponse (same status, status text, url
 * and headers) whose body is the text of the Blob instead of the Blob, which AbpHttpInterceptor passes on with no
 * dialog; the caller's own error path still runs. Every other request, every success and every other error is
 * passed through untouched.
 *
 * Pipeline Analytics' reads (CALLER_HANDLED_ENDPOINTS) are handed back differently: as an HttpResponse with the same status
 * and a `text/plain` Blob body, so the NSwag proxy can still read the server's error code (see the helper).
 *
 * It must run INSIDE AbpHttpInterceptor (provided after it, Angular's first interceptor being the outermost): provided
 * right after it in ServiceProxyModule (every module that imports it: AppModule, SettingsModule, ...) and in RootModule,
 * whose own AbpHttpInterceptor is the innermost of the root chain.
 */
@Injectable()
export class BackgroundCallErrorInterceptor implements HttpInterceptor {
    intercept(req: HttpRequest<unknown>, next: HttpHandler): Observable<HttpEvent<unknown>> {
        if (isCallerHandledCall(req.method, req.url)) {
            return next.handle(req).pipe(
                catchError((error: unknown) =>
                    error instanceof HttpErrorResponse &&
                    handsFailureToCaller(req.method, req.url, error.status, error.error instanceof Blob)
                        ? of(asTextAnswer(error))
                        : throwError(error)
                )
            );
        }
        if (!isSilencedBackgroundCall(req.method, req.url)) {
            return next.handle(req);
        }
        return next.handle(req).pipe(
            catchError((error: unknown) => {
                if (
                    !(error instanceof HttpErrorResponse) ||
                    !silencesBackgroundError(req.method, req.url, error.status, error.error instanceof Blob)
                ) {
                    return throwError(error);
                }
                return from(readBlobText(error.error as Blob)).pipe(
                    switchMap((text: string | null) => throwError(withTextBody(error, text)))
                );
            })
        );
    }
}

/** The text of an error body, or null when it cannot be read. */
export function readBlobText(blob: Blob): Promise<string | null> {
    if (typeof blob.text !== 'function') {
        return Promise.resolve(null);
    }
    return blob.text().catch(() => null);
}

/** The same failure with a plain-text body, which ABP's interceptor passes on without its dialog. */
export function withTextBody(error: HttpErrorResponse, text: string | null): HttpErrorResponse {
    return new HttpErrorResponse({
        error: text,
        headers: error.headers,
        status: error.status,
        statusText: error.statusText,
        url: error.url || undefined,
    });
}

/**
 * A caller-handled failure as an answer: the same status, headers and url, the same bytes in a `text/plain` Blob. ABP only
 * opens its dialog for a JSON Blob, and the proxy throws for any status but 200 with the text it reads from the Blob.
 */
export function asTextAnswer(error: HttpErrorResponse): HttpResponse<Blob> {
    return new HttpResponse<Blob>({
        body: new Blob([error.error as Blob], { type: 'text/plain' }),
        headers: error.headers,
        status: error.status,
        statusText: error.statusText,
        url: error.url || undefined,
    });
}
